import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const ExpenseTracker = ({ expenses = [], costs, participants = [], settings, onUpdate, onSettingsUpdate }) => {
  const [payments, setPayments] = useState([]);
  const [newPayment, setNewPayment] = useState({
    participantName: '',
    amount: 0,
    method: 'pix',
    status: 'pending',
    date: '',
    description: ''
  });

  const [paymentSettings, setPaymentSettings] = useState({
    pixKey: settings?.pixKey || '',
    pricePerPerson: settings?.pricePerPerson || 0,
    organizer: settings?.organizer || 'Você',
    splitMethod: 'equal', // equal, contribution, custom
    reminderEnabled: true
  });

  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showPixGenerator, setShowPixGenerator] = useState(false);

  useEffect(() => {
    setPayments(expenses);
  }, [expenses]);

  useEffect(() => {
    if (costs?.total && participants?.length > 0) {
      const pricePerPerson = costs?.total / participants?.length;
      setPaymentSettings(prev => ({
        ...prev,
        pricePerPerson
      }));
    }
  }, [costs, participants]);

  const paymentMethods = [
    { value: 'pix', label: 'PIX', icon: 'Smartphone' },
    { value: 'cash', label: 'Dinheiro', icon: 'Banknote' },
    { value: 'card', label: 'Cartão', icon: 'CreditCard' },
    { value: 'transfer', label: 'Transferência', icon: 'ArrowRightLeft' }
  ];

  const paymentStatus = [
    { value: 'pending', label: 'Pendente', color: 'text-yellow-600 bg-yellow-100' },
    { value: 'paid', label: 'Pago', color: 'text-green-600 bg-green-100' },
    { value: 'overdue', label: 'Atrasado', color: 'text-red-600 bg-red-100' }
  ];

  const addPayment = () => {
    if (!newPayment?.participantName || newPayment?.amount <= 0) return;
    
    const payment = {
      id: Date.now(),
      ...newPayment,
      date: newPayment?.date || new Date()?.toISOString()?.split('T')?.[0]
    };
    
    const updated = [...payments, payment];
    setPayments(updated);
    onUpdate(updated);
    
    setNewPayment({
      participantName: '',
      amount: 0,
      method: 'pix',
      status: 'pending',
      date: '',
      description: ''
    });
    setShowAddPayment(false);
  };

  const updatePayment = (id, field, value) => {
    const updated = payments?.map(payment => 
      payment?.id === id ? { ...payment, [field]: value } : payment
    );
    setPayments(updated);
    onUpdate(updated);
  };

  const removePayment = (id) => {
    const updated = payments?.filter(payment => payment?.id !== id);
    setPayments(updated);
    onUpdate(updated);
  };

  const generatePaymentsForAll = () => {
    const newPayments = participants?.map(participant => ({
      id: Date.now() + Math.random(),
      participantName: participant?.name,
      amount: paymentSettings?.pricePerPerson,
      method: 'pix',
      status: 'pending',
      date: new Date()?.toISOString()?.split('T')?.[0],
      description: 'Participação no churrasco'
    }));
    
    const updated = [...payments, ...newPayments];
    setPayments(updated);
    onUpdate(updated);
  };

  const sendPaymentReminder = (payment) => {
    const participant = participants?.find(p => p?.name === payment?.participantName);
    if (!participant?.phone) {
      alert('Participante não tem telefone cadastrado');
      return;
    }
    
    const message = `Oi ${participant?.name}! Lembrete: falta pagar R$ ${payment?.amount?.toFixed(2)} do churrasco. ${paymentSettings?.pixKey ? `PIX: ${paymentSettings?.pixKey}` : 'Entre em contato para detalhes do pagamento.'}`;
    
    window.open(`https://wa.me/${participant?.phone?.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`);
  };

  const generatePixCode = () => {
    if (!paymentSettings?.pixKey) {
      alert('Configure a chave PIX primeiro');
      return;
    }
    
    // Simulated PIX code generation (in real app, would integrate with payment provider)
    const pixCode = `00020126${paymentSettings?.pixKey?.length?.toString()?.padStart(2, '0')}${paymentSettings?.pixKey}5204000053039865802BR5913${paymentSettings?.organizer}6009SAO PAULO622905258031234567890123456789630400`;
    
    navigator.clipboard?.writeText(pixCode);
    alert('Código PIX copiado para a área de transferência!');
  };

  const updateSettings = (field, value) => {
    const updated = { ...paymentSettings, [field]: value };
    setPaymentSettings(updated);
    onSettingsUpdate(updated);
  };

  const totalPaid = payments?.filter(p => p?.status === 'paid')?.reduce((sum, p) => sum + p?.amount, 0);
  const totalPending = payments?.filter(p => p?.status === 'pending')?.reduce((sum, p) => sum + p?.amount, 0);
  const totalOverdue = payments?.filter(p => p?.status === 'overdue')?.reduce((sum, p) => sum + p?.amount, 0);

  const paidCount = payments?.filter(p => p?.status === 'paid')?.length;
  const pendingCount = payments?.filter(p => p?.status === 'pending')?.length;

  return (
    <div className="space-y-6">
      {/* Payment Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-success/10 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="CheckCircle" size={20} className="text-success" />
            <div>
              <p className="text-sm text-muted-foreground">Recebido</p>
              <p className="text-xl font-bold text-success">R$ {totalPaid?.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground">{paidCount} pessoas</p>
            </div>
          </div>
        </div>
        
        <div className="bg-warning/10 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="Clock" size={20} className="text-warning" />
            <div>
              <p className="text-sm text-muted-foreground">Pendente</p>
              <p className="text-xl font-bold text-warning">R$ {totalPending?.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground">{pendingCount} pessoas</p>
            </div>
          </div>
        </div>
        
        <div className="bg-destructive/10 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="AlertCircle" size={20} className="text-destructive" />
            <div>
              <p className="text-sm text-muted-foreground">Atrasado</p>
              <p className="text-xl font-bold text-destructive">R$ {totalOverdue?.toFixed(2)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-primary/10 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="DollarSign" size={20} className="text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Total Esperado</p>
              <p className="text-xl font-bold text-primary">
                R$ {(costs?.total || 0)?.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Payment Settings */}
      <div className="bg-card rounded-lg border border-border p-4">
        <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
          <Icon name="Settings" size={18} />
          Configurações de Pagamento
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Chave PIX"
            value={paymentSettings?.pixKey}
            onChange={(e) => updateSettings('pixKey', e?.target?.value)}
            placeholder="email@exemplo.com ou CPF"
            description="Para receber os pagamentos"
          />
          
          <Input
            label="Valor por Pessoa"
            type="number"
            step="0.01"
            value={paymentSettings?.pricePerPerson}
            onChange={(e) => updateSettings('pricePerPerson', parseFloat(e?.target?.value) || 0)}
            placeholder="0.00"
            description="Será calculado automaticamente"
          />
          
          <Input
            label="Organizador"
            value={paymentSettings?.organizer}
            onChange={(e) => updateSettings('organizer', e?.target?.value)}
            placeholder="Seu nome"
          />
          
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Método de Divisão
            </label>
            <select
              value={paymentSettings?.splitMethod}
              onChange={(e) => updateSettings('splitMethod', e?.target?.value)}
              className="w-full h-10 px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="equal">Divisão Igual</option>
              <option value="contribution">Por Contribuição</option>
              <option value="custom">Personalizada</option>
            </select>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2 mt-4">
          <Button
            variant="outline"
            onClick={() => setShowPixGenerator(!showPixGenerator)}
            iconName="Smartphone"
            disabled={!paymentSettings?.pixKey}
          >
            Gerar PIX
          </Button>
          
          <Button
            variant="outline"
            onClick={generatePaymentsForAll}
            iconName="Users"
            disabled={participants?.length === 0}
          >
            Criar Cobrança para Todos
          </Button>
        </div>
        
        {showPixGenerator && paymentSettings?.pixKey && (
          <div className="mt-4 p-4 bg-muted/30 rounded-lg">
            <h4 className="font-medium mb-2">PIX para Cobrança</h4>
            <div className="flex items-center gap-2">
              <code className="bg-background px-3 py-2 rounded text-sm flex-1 border">
                {paymentSettings?.pixKey}
              </code>
              <Button
                size="sm"
                onClick={() => {
                  navigator.clipboard?.writeText(paymentSettings?.pixKey);
                  alert('Chave PIX copiada!');
                }}
                iconName="Copy"
              />
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Valor: R$ {paymentSettings?.pricePerPerson?.toFixed(2)} por pessoa
            </p>
          </div>
        )}
      </div>
      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => setShowAddPayment(!showAddPayment)}
          iconName="Plus"
        >
          Registrar Pagamento
        </Button>
        
        <Button
          variant="outline"
          onClick={() => {
            const pendingPayments = payments?.filter(p => p?.status === 'pending');
            pendingPayments?.forEach(payment => {
              setTimeout(() => sendPaymentReminder(payment), 100);
            });
          }}
          iconName="Bell"
          disabled={pendingCount === 0}
        >
          Enviar Lembretes ({pendingCount})
        </Button>
      </div>
      {/* Add Payment Form */}
      {showAddPayment && (
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-foreground">Registrar Pagamento</h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowAddPayment(false)}
            >
              <Icon name="X" size={16} />
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Participante</label>
              <select
                value={newPayment?.participantName}
                onChange={(e) => setNewPayment(prev => ({ ...prev, participantName: e?.target?.value }))}
                className="w-full h-10 px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Selecionar participante...</option>
                {participants?.map(p => (
                  <option key={p?.id} value={p?.name}>{p?.name}</option>
                ))}
              </select>
            </div>
            
            <Input
              label="Valor"
              type="number"
              step="0.01"
              value={newPayment?.amount}
              onChange={(e) => setNewPayment(prev => ({ ...prev, amount: parseFloat(e?.target?.value) || 0 }))}
              placeholder="0.00"
            />
            
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Método</label>
              <select
                value={newPayment?.method}
                onChange={(e) => setNewPayment(prev => ({ ...prev, method: e?.target?.value }))}
                className="w-full h-10 px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {paymentMethods?.map(method => (
                  <option key={method?.value} value={method?.value}>{method?.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Status</label>
              <select
                value={newPayment?.status}
                onChange={(e) => setNewPayment(prev => ({ ...prev, status: e?.target?.value }))}
                className="w-full h-10 px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {paymentStatus?.map(status => (
                  <option key={status?.value} value={status?.value}>{status?.label}</option>
                ))}
              </select>
            </div>
            
            <Input
              label="Data"
              type="date"
              value={newPayment?.date}
              onChange={(e) => setNewPayment(prev => ({ ...prev, date: e?.target?.value }))}
            />
            
            <Input
              label="Observações"
              value={newPayment?.description}
              onChange={(e) => setNewPayment(prev => ({ ...prev, description: e?.target?.value }))}
              placeholder="Observações sobre o pagamento"
            />
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={addPayment}
              disabled={!newPayment?.participantName || newPayment?.amount <= 0}
              iconName="Plus"
              iconPosition="left"
            >
              Registrar
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowAddPayment(false)}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}
      {/* Payments List */}
      <div className="space-y-3">
        <h3 className="font-medium text-foreground">Pagamentos ({payments?.length})</h3>
        
        {payments?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="CreditCard" size={48} className="mx-auto mb-2 opacity-50" />
            <p>Nenhum pagamento registrado</p>
            <p className="text-sm">Registre os pagamentos ou crie cobranças automáticas</p>
          </div>
        ) : (
          <div className="space-y-2">
            {payments?.map((payment) => {
              const statusInfo = paymentStatus?.find(s => s?.value === payment?.status);
              const methodInfo = paymentMethods?.find(m => m?.value === payment?.method);
              
              return (
                <div
                  key={payment?.id}
                  className="bg-card rounded-lg border border-border p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <Icon name="User" size={20} className="text-primary" />
                        </div>
                        
                        <div className="flex-1">
                          <h4 className="font-medium text-foreground">
                            {payment?.participantName}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-lg font-bold text-foreground">
                              R$ {payment?.amount?.toFixed(2)}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded ${statusInfo?.color}`}>
                              {statusInfo?.label}
                            </span>
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Icon name={methodInfo?.icon} size={12} />
                              <span>{methodInfo?.label}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">{payment?.date}</p>
                          {payment?.status === 'pending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => sendPaymentReminder(payment)}
                              iconName="Bell"
                              className="mt-1"
                            >
                              Lembrar
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {payment?.description && (
                        <p className="text-sm text-muted-foreground mb-2">
                          {payment?.description}
                        </p>
                      )}
                      
                      <div className="flex gap-2">
                        {payment?.status === 'pending' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updatePayment(payment?.id, 'status', 'paid')}
                            iconName="CheckCircle"
                          >
                            Marcar como Pago
                          </Button>
                        )}
                        
                        {payment?.status === 'paid' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updatePayment(payment?.id, 'status', 'pending')}
                            iconName="Undo"
                          >
                            Desfazer
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removePayment(payment?.id)}
                      className="text-destructive hover:bg-destructive/10"
                    >
                      <Icon name="Trash2" size={16} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpenseTracker;